import { z } from 'zod';
import { brandGradient, semanticTokens } from './tokens.js';
/**
 * Branding de un tenant (pharma).
 *
 * Todo es opcional: lo que la pharma no define hereda del tema de Dx Tech. Eso
 * garantiza que un tenant recién creado ya se vea bien, en vez de arrancar con
 * una pantalla sin estilo que hay que "completar" antes de poder demostrar nada.
 */
const hexColor = z
    .string()
    .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Debe ser un color hexadecimal, por ejemplo #335CFF');
/**
 * Los logos se guardan como data URL (el logo de una pharma pesa pocos KB) o como
 * URL externa. Evita depender de object storage, que en Render tiene costo aparte.
 */
const imageSource = z
    .string()
    .refine((v) => v.startsWith('data:image/') || v.startsWith('https://'), 'Debe ser un data URL de imagen o una URL https');
export const tenantBrandingSchema = z.object({
    /** Nombre del programa tal como lo ve el paciente. Ej: "Vivir Mejor". */
    programName: z.string().min(1).max(80),
    logoUrl: imageSource.nullish(),
    logoDarkUrl: imageSource.nullish(),
    faviconUrl: imageSource.nullish(),
    primaryColor: hexColor.nullish(),
    primaryFgColor: hexColor.nullish(),
    accentColor: hexColor.nullish(),
    secondaryColor: hexColor.nullish(),
    bgColor: hexColor.nullish(),
    surfaceColor: hexColor.nullish(),
    surfaceTintedColor: hexColor.nullish(),
    fgColor: hexColor.nullish(),
    fgMutedColor: hexColor.nullish(),
    /** Familia tipográfica completa, con fallbacks. Vacío = Urbanist de Dx Tech. */
    fontFamily: z.string().max(200).nullish(),
    /** URL de Google Fonts si la pharma usa una tipografía propia. */
    fontUrl: z.string().url().nullish(),
    /** Gradiente de encabezados. Vacío = gradiente de Dx Tech. */
    gradient: z.string().max(300).nullish(),
    radiusScale: z.enum(['sharp', 'default', 'rounded']).default('default'),
    welcomeTitle: z.string().max(120).nullish(),
    welcomeText: z.string().max(600).nullish(),
    supportEmail: z.string().email().nullish(),
    supportPhone: z.string().max(40).nullish(),
    legalTerms: z.string().nullish(),
    legalPrivacy: z.string().nullish(),
    /**
     * Escotilla de escape para pedidos de marca que el editor no cubre.
     * Se inyecta con scope al contenedor del tenant, nunca global.
     */
    customCss: z.string().max(20_000).nullish(),
});
const RADIUS_SCALES = {
    sharp: { md: '4px', lg: '8px' },
    default: { md: '12px', lg: '20px' },
    rounded: { md: '18px', lg: '32px' },
};
/**
 * Traduce el branding de un tenant al conjunto de variables CSS que hay que
 * pisar. Devuelve solo lo que difiere del tema base — así el CSS inyectado se
 * mantiene chico y es fácil de inspeccionar en el navegador durante una demo.
 */
export function resolveTenantTheme(branding) {
    if (!branding)
        return {};
    const vars = {};
    const set = (token, value) => {
        if (!value)
            return;
        if (semanticTokens[token] === value)
            return; // idéntico al base: no hace falta pisarlo
        vars[`--tr-${token}`] = value;
    };
    set('primary', branding.primaryColor);
    set('primary-fg', branding.primaryFgColor);
    set('accent', branding.accentColor);
    set('secondary', branding.secondaryColor);
    set('bg', branding.bgColor);
    set('surface', branding.surfaceColor);
    set('surface-tinted', branding.surfaceTintedColor);
    set('fg', branding.fgColor);
    set('fg-muted', branding.fgMutedColor);
    set('font-sans', branding.fontFamily);
    set('gradient', branding.gradient);
    // Si la pharma eligió un primario pero no un hover, lo derivamos oscureciéndolo.
    // Sin esto, el hover se quedaría en el azul de Dx Tech sobre un botón de otro
    // color, que es el defecto más visible de un branding a medio aplicar.
    if (branding.primaryColor) {
        vars['--tr-primary-hover'] = shade(branding.primaryColor, -0.16);
        vars['--tr-primary-soft'] = mixWithWhite(branding.primaryColor, 0.9);
    }
    if (branding.accentColor) {
        vars['--tr-accent-soft'] = mixWithWhite(branding.accentColor, 0.92);
    }
    const scale = RADIUS_SCALES[branding.radiusScale ?? 'default'];
    if (branding.radiusScale && branding.radiusScale !== 'default') {
        vars['--tr-radius-md'] = scale.md;
        vars['--tr-radius-lg'] = scale.lg;
    }
    return vars;
}
/** Serializa las variables resueltas a un bloque CSS con scope. */
export function tenantThemeToCss(vars, selector = ':root') {
    const entries = Object.entries(vars);
    if (entries.length === 0)
        return '';
    const body = entries.map(([k, v]) => `  ${k}: ${v};`).join('\n');
    return `${selector} {\n${body}\n}`;
}
export const DEFAULT_GRADIENT = brandGradient;
// ── Utilidades de color ──────────────────────────────────────────────────────
function parseHex(hex) {
    let h = hex.replace('#', '');
    if (h.length === 3)
        h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    return [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
    ];
}
function toHex(rgb) {
    return '#' + rgb.map((c) => Math.round(Math.max(0, Math.min(255, c))).toString(16).padStart(2, '0')).join('');
}
/** amount < 0 oscurece, > 0 aclara. */
export function shade(hex, amount) {
    const [r, g, b] = parseHex(hex);
    const t = amount < 0 ? 0 : 255;
    const p = Math.abs(amount);
    return toHex([r + (t - r) * p, g + (t - g) * p, b + (t - b) * p]);
}
/** Mezcla con blanco. ratio 0.9 = 90% blanco. Sirve para las variantes "soft". */
export function mixWithWhite(hex, ratio) {
    const [r, g, b] = parseHex(hex);
    return toHex([r + (255 - r) * ratio, g + (255 - g) * ratio, b + (255 - b) * ratio]);
}
/**
 * Luminancia relativa según WCAG 2.1.
 * La usa el editor de branding para avisarle a la pharma cuando su color elegido
 * no tiene contraste suficiente — en una app que usan pacientes cursando
 * quimioterapia, con fatiga visual y a veces visión borrosa, el contraste no es
 * una preferencia estética.
 */
export function relativeLuminance(hex) {
    const [r, g, b] = parseHex(hex).map((c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const [hi, lo] = la > lb ? [la, lb] : [lb, la];
    return (hi + 0.05) / (lo + 0.05);
}
/** Elige negro o blanco como color de texto sobre un fondo dado. */
export function readableForeground(background) {
    return contrastRatio(background, '#FFFFFF') >= contrastRatio(background, '#23293B') ? '#FFFFFF' : '#23293B';
}
export const WCAG_AA_NORMAL = 4.5;
export const WCAG_AA_LARGE = 3;
//# sourceMappingURL=branding.js.map